import { faCheck, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useReactiveVar } from '@apollo/client';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';

import FilterDropdown from '../FilterDropdown';
import FilterPill from '../FilterPill';
import { buildMultiChipLabel } from '../helpers';

import { useGetCustomerAndSkillsQuery } from '@/graphql';
import { currentAgencyVar } from '@/util/apollo/cache';

type Props = {
  value: string[] | undefined;
  onApply: (next: string[] | undefined) => void;
  onClear: () => void;
};

const SkillsPill = ({ value, onApply, onClear }: Props) => {
  const currentAgency = useReactiveVar(currentAgencyVar);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [staged, setStaged] = useState<string[]>(() => value || []);
  const [search, setSearch] = useState('');

  const { data } = useGetCustomerAndSkillsQuery({
    variables: { agencyId: currentAgency!.id, filters: {} },
  });

  const skills = useMemo(() => {
    const list: { id: string; name: string }[] = [];
    data?.agency.skillCategories.forEach((cat) => {
      cat.skills.forEach((s) => list.push({ id: s.id, name: s.name }));
    });
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const filteredSkills = useMemo(() => {
    if (!search) return skills;
    const q = search.toLowerCase();
    return skills.filter((s) => s.name.toLowerCase().includes(q));
  }, [skills, search]);

  useEffect(() => {
    if (open) {
      setStaged(value || []);
      setSearch('');
    }
  }, [open, value]);

  const toggleItem = (id: string) => {
    setStaged((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  const apply = () => {
    onApply(staged.length > 0 ? staged : undefined);
    setOpen(false);
  };

  const selectedNames = (value || [])
    .map((id) => skills.find((s) => s.id === id)?.name)
    .filter((n): n is string => Boolean(n));
  const isActive = selectedNames.length > 0;
  const { label, more } = buildMultiChipLabel(selectedNames);

  return (
    <div className="pill-wrap" ref={wrapRef}>
      <FilterPill
        placeholder="Skills"
        chipLabel={isActive ? label : undefined}
        chipMore={isActive ? more : undefined}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        onClear={isActive ? onClear : undefined}
        a11yLabel="Filter by skills"
      />
      <FilterDropdown
        open={open}
        onClose={() => setOpen(false)}
        onApply={apply}
        anchorRef={wrapRef}
        panelStyle={{ minWidth: 260 }}
      >
        <div className="dropdown-search">
          <FontAwesomeIcon icon={faSearch} color="#979797" />
          <input
            type="text"
            placeholder="Search skill"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="dropdown-scroll">
          {filteredSkills.map((s) => {
            const checked = staged.includes(s.id);
            return (
              <div
                key={s.id}
                className="dropdown-option"
                role="checkbox"
                aria-checked={checked}
                onClick={() => toggleItem(s.id)}
              >
                <span className={classNames('dropdown-check', { checked })}>
                  {checked ? (
                    <FontAwesomeIcon icon={faCheck} fixedWidth />
                  ) : null}
                </span>
                {s.name}
              </div>
            );
          })}
        </div>
      </FilterDropdown>
    </div>
  );
};

export default SkillsPill;
